import { MagentoFilterManager } from "~/modules/catalog/classes/MagentoFilterManager";
import type { Aggregation } from "~/types/catalog/Aggregation";

export async function useMagentoCatalog(
  categoryId: MaybeRef<string | undefined>,
  queryVariables: {
    queryVariables: any;
    committedQueryVariables: Ref<any>;
  },
  keySuffix?: string,
) {
  const { queryVariables: filters, committedQueryVariables } = queryVariables;

  const productsResult = useMagentoCatalogData(categoryId, queryVariables, {
    key: keySuffix ? `magento-products-${keySuffix}` : undefined,
  });
  const filterlessResult = useMagentoCatalogFilterlessAggData(
    categoryId,
    computed(() => filters.term),
    { key: keySuffix ? `magento-filterless-agg-${keySuffix}` : undefined },
  );
  const filteredResult = useMagentoCatalogAggregationsData(
    categoryId,
    queryVariables,
    { key: keySuffix ? `magento-agg-${keySuffix}` : undefined },
  );

  // Await all useAsyncData promises to ensure data is available before render
  // (prevents SSR/client hydration mismatch)
  await Promise.all([
    productsResult.asyncData,
    filterlessResult.asyncData,
    filteredResult.asyncData,
  ]);

  const {
    data: catalogDataResponse,
    pending: catalogPending,
    refresh: refreshCatalogData,
    error: catalogError,
    products: productList,
    totalCount,
    pageInfo,
  } = productsResult;
  const { aggregations: filterlessAggregations } = filterlessResult;
  const { aggregations: aggregationsList } = filteredResult;

  const isNoProducts = computed(() => !productList.value.length);

  const aggregations = computed<Aggregation[]>(() => {
    return mergeAggregations(
      aggregationsList.value ?? [],
      filterlessAggregations.value ?? [],
    );
  });

  function applyAggregations() {
    filters.filter.applyFromAggregations(aggregations.value);
    committedQueryVariables.value.filter.applyFromAggregations(
      aggregations.value,
    );
  }

  async function refreshPage() {
    await refreshCatalogData();
    applyAggregations();
  }

  /** Restore filter state in FilterManager class from ssr hydration data **/
  if (import.meta.client && aggregations.value) {
    filters.filter.applyFromAggregations(aggregations.value);
    committedQueryVariables.value.filter.setState(
      filters.filter.getCurrentState(),
    );
  } else if (import.meta.server) {
    applyAggregations();
  }

  function mergeAggregations(
    catalogSearchAggregations: Aggregation[],
    filterlessAggregations: Aggregation[],
  ): Aggregation[] {
    const catalogSearchAggregationsMap = new Map(
      catalogSearchAggregations.map((agg) => [agg.attribute_code, agg]),
    );

    const selectedFilters: Array<{ key: string; value: string }> =
      filters.filter.selectedFilters();

    return filterlessAggregations
      .map((agg) => {
        if (MagentoFilterManager.isNumberAggregation(agg)) {
          return agg;
        }

        const catalogFilter = catalogSearchAggregationsMap.get(
          agg.attribute_code,
        );

        if (!catalogFilter) {
          const selectedOptionsKeys = selectedFilters
            .filter((f: { key: string }) => f.key === agg.attribute_code)
            .map((f: { value: string }) => f.value);

          if (selectedOptionsKeys.length) {
            return {
              ...agg,
              options: agg.options.filter((o) =>
                selectedOptionsKeys.includes(o.value),
              ),
            };
          }
        }

        return catalogFilter;
      })
      .filter(Boolean) as Aggregation[];
  }

  watch(aggregationsList, () => {
    applyAggregations();
  });

  return {
    catalogDataResponse,
    catalogPending,
    catalogError,
    productList,
    totalCount,
    pageInfo,
    isNoProducts,
    aggregations,
    filterlessAggregations,
    refreshPage,
  };
}
